import { AddAPhoto, Delete, MoreVert, Star } from '@mui/icons-material';
import Button from 'components/CustomButtons/Button.js';
import React, { useEffect, useRef, useState } from 'react';
import styles from './SingleImageUpload.module.scss';
import classNames from 'classnames';
import ImageCropModal from 'components/ImageCropModal/ImageCropModal.jsx';

function extractAspectStr(style, aspectRatio) {
    if (aspectRatio) return String(aspectRatio);
    if (style && style.aspectRatio) return String(style.aspectRatio);
    return '3/2';
}

export default function SingleImageUpload({
    image,
    onUpload,
    onDelete,
    onError,
    index,
    id,
    overlay,
    style,
    aspectRatio,
    imgHolder,
    disabledDeleteBtn,
    onChangeOrder,
    onSetAsMain,
    offer,
    cropShape = 'rect',
    disableCrop = false,
    allowReplace = false,
    replaceLabel,
}) {
    const [isDragOver, setIsDragOver] = useState(false);
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [pendingImageSrc, setPendingImageSrc] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const fileInputRef = useRef(null);
    const menuRef = useRef(null);

    const aspectStr = extractAspectStr(style, aspectRatio);

    useEffect(() => {
        if (!menuOpen) return;
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen]);

    const handleDragOver = () => setIsDragOver(true);
    const handleDragLeave = () => setIsDragOver(false);
    const handleDrop = () => setIsDragOver(false);

    const handleImageChange = (e) => {
        e.preventDefault();
        const newFile = e.target.files[0];
        if (!newFile) return;

        const fileTypes = ['jpg', 'jpeg', 'png'];
        const extension = newFile.name.split('.').pop().toLowerCase();

        if (!fileTypes.includes(extension)) {
            alert('Skráin verður að vera af gerðinni jpg, jpeg eða png.');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            if (disableCrop) {
                onUpload(reader.result, index, id);
            } else {
                setPendingImageSrc(reader.result);
                setCropModalOpen(true);
            }
        };
        reader.onerror = () => {
            if (onError) onError();
        };
        reader.readAsDataURL(newFile);

        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleCropConfirm = (croppedBase64) => {
        setCropModalOpen(false);
        setPendingImageSrc(null);
        onUpload(croppedBase64, index, id);
    };

    const handleCropCancel = () => {
        setCropModalOpen(false);
        setPendingImageSrc(null);
    };

    const isEmpty = image === null || image === undefined || image === '';
    const showDropdownMenu = onSetAsMain != null;
    const roundOuterStyle = cropShape === 'round' ? { borderRadius: '50%' } : {};
    const roundInnerStyle = cropShape === 'round' ? { borderRadius: '50%' } : {};

    return (
        <>
            <ImageCropModal
                open={cropModalOpen}
                imageSrc={pendingImageSrc}
                aspectStr={aspectStr}
                offer={offer}
                cropShape={cropShape}
                onConfirm={handleCropConfirm}
                onCancel={handleCropCancel}
            />

            {isEmpty ? (
                <div
                    style={{ ...style, ...(aspectRatio != null ? { aspectRatio } : {}), ...roundOuterStyle }}
                    className={classNames(
                        styles.emptyContainer,
                        isDragOver ? styles.drag_over : ''
                    )}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <AddAPhoto
                        style={{
                            height: 30,
                            width: 30,
                            margin: 0,
                            color: 'var(--spara-primary, #de2c48)',
                        }}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={handleImageChange}
                    />
                </div>
            ) : (
                // outerWrapper has position:relative but NO overflow:hidden so the dropdown can escape
                <div
                    style={{ ...style, ...(aspectRatio != null ? { aspectRatio } : {}), ...roundOuterStyle }}
                    className={styles.outerWrapper}
                >
                    <div
                        style={roundInnerStyle}
                        className={classNames(styles.container, isDragOver ? styles.drag_over : '')}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <img
                            src={image}
                            alt=""
                            onError={(e) => {
                                console.error('Error loading image:', e);
                                if (onError) onError();
                            }}
                        />
                        {overlay}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            onChange={handleImageChange}
                        />
                    </div>

                    {!showDropdownMenu && (
                        <div className={styles.actionButtons}>
                            {allowReplace && (
                                <Button
                                    color="white"
                                    justIcon
                                    className={styles.replaceButtonSmall}
                                    onClick={() => fileInputRef.current?.click()}
                                    aria-label={replaceLabel || 'Replace image'}
                                >
                                    <AddAPhoto
                                        style={{
                                            height: 18,
                                            width: 18,
                                            margin: 0,
                                            color: '#333',
                                            fill: '#333',
                                        }}
                                    />
                                </Button>
                            )}
                            {!disabledDeleteBtn && (
                                <Button
                                    color="danger"
                                    justIcon
                                    className={styles.deleteButtonSmall}
                                    onClick={() => onDelete(index)}
                                    aria-label="Delete image"
                                >
                                    <Delete
                                        style={{
                                            height: 18,
                                            width: 18,
                                            margin: 0,
                                            color: '#fff',
                                            fill: '#fff',
                                        }}
                                    />
                                </Button>
                            )}
                        </div>
                    )}

                    {showDropdownMenu && (
                        <div ref={menuRef} className={styles.menuWrapper}>
                            <button
                                type="button"
                                className={styles.menuButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuOpen((v) => !v);
                                }}
                            >
                                <MoreVert style={{ fontSize: 20 }} />
                            </button>
                            {menuOpen && (
                                <div className={styles.dropdown}>
                                    <button
                                        type="button"
                                        className={styles.dropdownItem}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMenuOpen(false);
                                            onSetAsMain(index);
                                        }}
                                    >
                                        <Star style={{ fontSize: 16 }} />
                                        Setja sem aðalmynd
                                    </button>
                                    {!disabledDeleteBtn && (
                                        <button
                                            type="button"
                                            className={classNames(
                                                styles.dropdownItem,
                                                styles.dropdownItemDanger
                                            )}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setMenuOpen(false);
                                                onDelete(index);
                                            }}
                                        >
                                            <Delete style={{ fontSize: 16 }} />
                                            Eyða mynd
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
